import { motion, useReducedMotion, type HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost";
type Size = "sm" | "md";

type SharedProps = {
  variant?: Variant;
  size?: Size;
};

const base =
  "inline-flex items-center justify-center gap-2 rounded-2xl select-none whitespace-nowrap";

const focus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/55 focus-visible:ring-offset-2 focus-visible:ring-offset-bg";

const sizes: Record<Size, string> = {
  sm: "px-4 py-3 text-[15px] font-semibold",
  md: "px-6 py-3.5 text-[15px] font-semibold"
};

const variants: Record<Variant, string> = {
  primary: cn(
    "bg-gradient-to-r from-accent/95 to-accent2/90 text-bg",
    "shadow-[0_18px_70px_rgba(0,140,255,0.18)]",
    "transition hover:brightness-110"
  ),
  secondary: cn(
    "border border-border/14 bg-panel/18 text-fg/90",
    "transition hover:border-border/20 hover:bg-panel/26"
  ),
  ghost: cn(
    "text-fg/85",
    "transition hover:text-fg hover:bg-panel/10"
  )
};

export type ButtonProps = HTMLMotionProps<"button"> & SharedProps;

export function Button({
  variant = "secondary",
  size = "md",
  type = "button",
  className,
  ...props
}: ButtonProps) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.button
      whileTap={reduceMotion ? undefined : { scale: 0.985 }}
      whileHover={reduceMotion ? undefined : { y: -1 }}
      className={cn(base, focus, sizes[size], variants[variant], className)}
      type={type}
      {...props}
    />
  );
}

export type ButtonLinkProps = HTMLMotionProps<"a"> & SharedProps;

export function ButtonLink({
  variant = "secondary",
  size = "md",
  className,
  ...props
}: ButtonLinkProps) {
  const reduceMotion = useReducedMotion();
  return (
    <motion.a
      whileTap={reduceMotion ? undefined : { scale: 0.985 }}
      whileHover={reduceMotion ? undefined : { y: -1 }}
      className={cn(base, focus, sizes[size], variants[variant], className)}
      {...props}
    />
  );
}

